import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { API_BASE } from './api-base';
import { parseConversationReadState } from './chat-read-state';
import { apiFetch } from './api-client';
import type { ConversationReadState } from './chats';
import type { Message } from './use-chat';
import type { TurnActivitySignal } from './turn-activity';
import { useDuplexer } from './use-duplexer';
import { chatHistoryCache as cache } from './chat-session';
import { cachedHistoryWindow, prependHistoryWindow, refreshHistoryWindow } from './chat-history-window';
import { queryClient, queryKeys } from './query-client';

export interface ChatHistoryOptions { enabled: boolean; owner: string | null; targetMessageId: string | null; visible: boolean }
interface Window {
  messages: Message[]; olderCursor: string | null; readThroughToken: string | null;
  readState: ConversationReadState; asOf: string; turn?: TurnActivitySignal;
}
export function clearChatHistoryCache() { cache.clear(); }
const keyFor = (owner: string, id: string, target: string | null) => JSON.stringify([owner, id, target]);

/** Controls the bounded history window inside the existing chat/stream instance. */
export function useChatHistory(options: ChatHistoryOptions | undefined, id: string | null, streaming: boolean,
  messages: Message[], setMessages: Dispatch<SetStateAction<Message[]>>, setLoading: Dispatch<SetStateAction<boolean>>,
  setError: Dispatch<SetStateAction<string | null>>, setTurn: Dispatch<SetStateAction<TurnActivitySignal | null>>) {
  const [window, setWindow] = useState<Window | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const sequence = useRef(0);
  const active = useRef({ options, id, streaming });
  active.current = { options, id, streaming };
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const enabled = options?.enabled === true;
  const owner = options?.owner ?? null;
  const target = options?.targetMessageId ?? null;
  const key = enabled && owner && id ? keyFor(owner, id, target) : null;
  const currentKey = useRef(key);
  const paintedKey = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const earlier = useRef<{ key: string; cursor: string | null } | null>(null);
  currentKey.current = key;

  const fetchWindow = useCallback(async (conversationId: string, targetId: string | null, cursor?: string, signal?: AbortSignal) => queryClient.fetchQuery({
    queryKey: queryKeys.chatWindow(owner, conversationId, targetId, cursor), staleTime: 0, cacheTime: 10 * 60_000,
    queryFn: async ({ signal: querySignal }) => {
    const requestSignal = signal ?? querySignal;
    const params = new URLSearchParams({ contract: 'chats-v1', requestId: crypto.randomUUID(), limit: '40' });
    if (targetId && !cursor) params.set('targetMessageId', targetId);
    if (cursor) params.set('cursor', cursor);
    const response = await apiFetch(`${API_BASE}/api/conversations/${conversationId}/messages?${params}`, { credentials: 'include', signal: requestSignal });
    if (response.status === 501 && !targetId && !cursor) {
      const unsupported = await response.json().catch(() => null) as { error?: string } | null;
      if (unsupported?.error === 'chats_not_supported') {
        const legacy = await apiFetch(`${API_BASE}/api/conversations/${conversationId}/messages`, { credentials: 'include', signal: requestSignal });
        if (!legacy.ok) throw new Error(`Chat history request failed (${legacy.status})`);
        const data = await legacy.json() as { messages?: Message[]; turn?: TurnActivitySignal };
        return { messages: data.messages ?? [], turn: data.turn, olderCursor: null, readThroughToken: null, asOf: '',
          readState: { lastReadAt: null, lastReadCursor: null, hasUnreadMessages: null, hasUnreadNotifications: null,
            versions: { reader: null, messageHead: null, notifications: null } } };
      }
    }
    if (!response.ok) throw new Error(`Chat history request failed (${response.status})`);
    const data = await response.json() as Window;
    if (!Array.isArray(data.messages) || !data.asOf || !data.readState) throw new Error('Invalid chat history response');
    data.readState = parseConversationReadState(data.readState);
    if (targetId || cursor) data.readThroughToken = null;
    return data;
    },
  }), [owner]);

  useEffect(() => {
    const epoch = ++sequence.current;
    if (!enabled) {
      setWindow(null);
      setLoadingOlder(false);
      paintedKey.current = null;
      return;
    }
    const controller = new AbortController();
    const cancel = () => {
      if (id) void queryClient.cancelQueries({ queryKey: queryKeys.chatWindow(owner, id, target), exact: true });
      controller.abort();
    };
    setWindow(null);
    setLoadingOlder(false);
    if (!key || !id) {
      paintedKey.current = null;
      setMessages([]);
      setLoading(Boolean(id));
      return cancel;
    }
    // A route update after creating this conversation must not replace an active stream.
    if (active.current.streaming || !options?.visible) return cancel;
    const cached = cache.get(key);
    const changed = paintedKey.current !== key;
    if (changed) earlier.current = null;
    paintedKey.current = key;
    if (changed) setMessages(cached ?? []);
    setLoading(changed && !cached);
    setError(null);
    void fetchWindow(id, target, undefined, controller.signal).then((data) => {
      if (epoch !== sequence.current || currentKey.current !== key || active.current.streaming) return;
      const retained = refreshHistoryWindow(messagesRef.current, data.messages, data.olderCursor,
        earlier.current?.cursor ?? null, earlier.current?.key === key);
      if (retained.messages === data.messages) earlier.current = null;
      setMessages(retained.messages);
      setWindow({ ...data, ...retained });
      setTurn(data.turn ?? null);
      cache.delete(key);
      cache.set(key, cachedHistoryWindow(data.messages));
      while (cache.size > 20) cache.delete(cache.keys().next().value as string);
    }).catch((error: unknown) => {
      if (!controller.signal.aborted && epoch === sequence.current) setError(error instanceof Error ? error.message : String(error));
    }).finally(() => {
      if (epoch === sequence.current) setLoading(false);
    });
    return cancel;
  }, [enabled, owner, key, id, target, refresh, streaming, options?.visible, fetchWindow, setMessages, setLoading, setError, setTurn]);

  const reload = useCallback(async () => {
    if (!active.current.options?.enabled || active.current.streaming) return;
    setRefresh(value => value + 1);
  }, []);
  const scheduleReload = useCallback(() => {
    const state = active.current;
    if (!state.options?.enabled || !state.options.visible || state.options.targetMessageId || state.streaming
      || document.visibilityState !== 'visible') return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { void reload(); }, 600);
  }, [reload]);
  useDuplexer((entity) => { if (entity.conversationId === active.current.id) scheduleReload(); });
  useEffect(() => {
    if (!enabled || !options?.visible || target) return;
    const onVisible = () => { if (document.visibilityState === 'visible') scheduleReload(); };
    document.addEventListener('visibilitychange', onVisible);
    const poll = setInterval(scheduleReload, 30_000);
    return () => { document.removeEventListener('visibilitychange', onVisible); clearTimeout(timer.current); clearInterval(poll); };
  }, [enabled, options?.visible, target, scheduleReload]);

  const loadOlder = useCallback(async () => {
    if (!active.current.options?.enabled || !key || !id || !window?.olderCursor || loadingOlder || streaming) return;
    const epoch = sequence.current;
    const cursor = window.olderCursor;
    setLoadingOlder(true);
    try {
      const older = await fetchWindow(id, null, cursor);
      if (sequence.current !== epoch || currentKey.current !== key) return;
      if (older.olderCursor === cursor) throw new Error('Repeated chat history cursor');
      earlier.current = { key, cursor: older.olderCursor };
      setMessages(current => {
        const merged = prependHistoryWindow(current, older.messages);
        if (!target) cache.set(key, cachedHistoryWindow(merged));
        return merged;
      });
      setWindow(current => current && ({ ...current, olderCursor: older.olderCursor }));
    } catch (error) {
      if (sequence.current === epoch) setError(error instanceof Error ? error.message : String(error));
    } finally { if (sequence.current === epoch) setLoadingOlder(false); }
  }, [key, id, target, window?.olderCursor, loadingOlder, streaming, fetchWindow, setMessages, setError]);

  // Stream recovery uses the same bounded API but never acknowledges an unrendered response.
  const fetchLatest = useCallback(async (conversationId: string) =>
    (await fetchWindow(conversationId, null)).messages, [fetchWindow]);

  return { enabled, historical: enabled && !!target, targetMessageId: target, olderCursor: enabled ? window?.olderCursor ?? null : null,
    loadingOlder, loadOlder, reload, fetchLatest, readThroughToken: enabled ? window?.readThroughToken ?? null : null,
    readState: enabled ? window?.readState ?? null : null };
}
